/* eslint-disable max-len */
import {
  Button,
  Grid,
  Paper,
  Typography
} from '@mui/material';
import { Stack } from '@mui/system';
import { ContainerForm } from 'components/shared/container_form';
import InputComLabelFormik from 'components/shared/custom_input_label_formik';
import DocumentComponent from 'components/shared/document_component';
import { PageTitle } from 'components/shared/page_title';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { validarCPF, validarCNPJ } from 'utils/uteis';
import SectionTable from '../shared/SectionTable';
import {
  useSchoolFeedingAcquisitions,
  SecaoAquisicao
} from '../shared/useSchoolFeedingAcquisitions';

// Re-export pure helpers for backward compatibility with existing tests
export {
  formatarValorParaExibicaoFn,
  formatarValorInputFn,
  getSecaoKeyFn,
  calcularTotalSecaoFn,
  calcularTotalGeralFn,
  isFormValidFn,
  criarSecoesComProdutoAdicionado
} from '../shared/useSchoolFeedingAcquisitions';


const DescricaoGrupo: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <Typography
    id={id}
    variant="body2"
    color="text.secondary"
    sx={{ mb: 2, mt: -1 }}
  >
    {children}
  </Typography>
);


interface Step1Props {
  initialData: SecaoAquisicao[];
  onNext: () => void;
  formik: any;
}

const Step1: React.FC<Step1Props> = ({ initialData, onNext, formik }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const match = /cifa-(\d{4})/.exec(location.pathname);
  const anoReferencia = match ? Number(match[1]) : 2026;
  const anoAnterior = anoReferencia - 1;

  const {
    secoes,
    setSecoes,
    novosProdutos,
    formatarValorParaExibicao,
    getSecaoKey,
    calcularTotalSecao,
    handleInputChange,
    handleClearFields,
    handleAddProduto,
    handleRemoveProdutoCnpj,
    handleRemoveProdutoCpf,
  } = useSchoolFeedingAcquisitions({
    initialData,
    formik
  });

  // Sincronizar dados iniciais do formik com o estado local
  useEffect(() => {
    if (formik.values.secoesAquisicao && formik.values.secoesAquisicao.length > 0) {
      setSecoes(formik.values.secoesAquisicao);
    } else {
      formik.setFieldValue('secoesAquisicao', initialData);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sincronizar mudanças do initialData (quando dados do backend chegam)
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      const hasBackendData = initialData.some(secao =>
        secao.produtosCnpj.length > 0 || secao.produtosCpf.length > 0
      );

      if (hasBackendData) {
        setSecoes(initialData);
        formik.setFieldValue('secoesAquisicao', initialData);
      }
    }
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = () => {
    formik.setFieldValue('secoesAquisicao', secoes);
    onNext();
  };


  return (
    <>
      <PageTitle
        title='Valores para Aquisição de Alimentação Escolar - Em 2026'
        subtitle='Informe corretamente os dados sobre os valores para aquisição de alimentação escolar em 2026.'
      />

      <Stack marginX='4rem' marginTop='1rem' spacing='2rem' flexDirection='column' maxWidth='1152px' width='100%'>



        <ContainerForm title='Valores Aplicados nas Aquisições da Agricultura Familiar'>

          <DescricaoGrupo id="desc-agricultura">
            Referente ao que foi comprado exclusivamente de produtos da agricultura familiar para alimentação escolar no ano de {anoAnterior}.
          </DescricaoGrupo>

          <Grid container spacing={3} sx={{ width: '100%', mb: 3 }}>
            <Grid item xs={12} md={4}>

              <InputComLabelFormik
                label='alimentacao_af_recurso_pnae'
                labelTexto='Recurso do PNAE Federal (R$)'
                formik={formik}
                placeholder='0,00'
                required={true}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <InputComLabelFormik
                label='alimentacao_af_recurso_estado'
                labelTexto='Recurso do Governo do Estado (R$) - Opcional'
                formik={formik}
                placeholder='0,00'
                required={false}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <InputComLabelFormik
                label='alimentacao_af_recurso_prefeitura'
                labelTexto='Recurso da Prefeitura (R$)'
                formik={formik}
                placeholder='0,00'
                required={true}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 3, pt: 2, }}>
              <Typography variant='body1' color='text.primary' sx={{ fontWeight: 500 }}>
                Total de Recursos Aplicados nas Aquisições da Agricultura Familiar:
                <Typography component='span' variant='h6' color='primary' sx={{ ml: 1, fontWeight: 600 }}>
                  R$ {formatarValorParaExibicao(Number(formik.values.alimentacao_af_recurso_pnae || 0) + Number(formik.values.alimentacao_af_recurso_estado || 0) + Number(formik.values.alimentacao_af_recurso_prefeitura || 0))}
                </Typography>
              </Typography>
            </Grid>
          </Grid>

        </ContainerForm>

        <ContainerForm title='Valores Totais Aplicados na Alimentação Escolar'>

          <DescricaoGrupo id="desc-totais">
            Referente ao total de recursos aplicados na alimentação escolar no município (independentemente da origem dos fornecedores).
          </DescricaoGrupo>

          <Grid container spacing={3} sx={{ width: '100%', mb: 3 }}>
            <Grid item xs={12} md={4}>
              <InputComLabelFormik
                label='alimentacao_total_recurso_pnae'
                labelTexto='Total de Recursos PNAE Federal (R$)'
                formik={formik}
                placeholder='0,00'
                required={true}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <InputComLabelFormik
                label='alimentacao_total_recurso_estado'
                labelTexto='Total de Recursos do Governo do Estado (R$) - Opcional'
                formik={formik}
                placeholder='0,00'
                required={false}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <InputComLabelFormik
                label='alimentacao_total_recurso_prefeitura'
                labelTexto='Total de Recursos da Prefeitura (R$)'
                formik={formik}
                placeholder='0,00'
                required={true}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 3, pt: 2 }}>
              <Typography variant='body1' color='text.primary' sx={{ fontWeight: 500 }}>
                Total Geral de Recursos na Alimentação Escolar:
                <Typography component='span' variant='h6' color='primary' sx={{ ml: 1, fontWeight: 600 }}>
                  R$ {formatarValorParaExibicao(Number(formik.values.alimentacao_total_recurso_pnae || 0) + Number(formik.values.alimentacao_total_recurso_estado || 0) + Number(formik.values.alimentacao_total_recurso_prefeitura || 0))}
                </Typography>
              </Typography>
            </Grid>
          </Grid>

        </ContainerForm>

        <ContainerForm title='Ata de Aprovação dos Dados de Alimentação Escolar'>

          <Grid container spacing={3} sx={{ width: '100%' }}>
            <Grid item xs={12}>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Faça o upload da ata da reunião que aprova os dados de alimentação escolar informados acima.
              </Typography>

              <DocumentComponent
                formik={formik}
                formikLabel='alimentacao_ata_aprovacao_uuid'
                key="ata-aprovacao-alimentacao-component"
                uuid={formik.values.alimentacao_ata_aprovacao_uuid}
                setUuid={(uuid) => formik.setFieldValue('alimentacao_ata_aprovacao_uuid', uuid)}
                podeSubstituir={true}
                title="Ata de Aprovação - Alimentação Escolar"
              />
            </Grid>
          </Grid>

        </ContainerForm>

        <PageTitle
          title="Aquisição de alimentação escolar"
          subtitle="Detalhamento dos valores aplicados para aquisição de alimentação escolar"
        />

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            backgroundColor: 'grey.50',
            borderLeft: '4px solid',
            borderLeftColor: 'primary.main',
            mb: 2
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ m: 0, lineHeight: 1.55, letterSpacing: 0.15 }}
          >
            O detalhamento dos valores aqui discriminados se referem <strong>EXCLUSIVAMENTE</strong> aos
            valores aplicados à aquisição de produtos da <strong>AGRICULTURA FAMILIAR</strong> para a
            alimentação escolar da rede municipal.
          </Typography>
        </Paper>

        {secoes.map((secao, secaoIndex) => (
          <ContainerForm key={getSecaoKey(secao, secaoIndex)} title={secao.titulo}>
            <SectionTable
              tipo="cnpj"
              secaoIndex={secaoIndex}
              secao={secao}
              novosProdutos={novosProdutos}
              handleInputChange={handleInputChange}
              handleClearFields={handleClearFields}
              handleAddProduto={handleAddProduto}
              handleRemoveProduto={(i, t, id) => t === 'cnpj' ? handleRemoveProdutoCnpj(i, id) : handleRemoveProdutoCpf(i, id)}
              validarCPF={validarCPF}
              validarCNPJ={validarCNPJ}
            />
            <SectionTable
              tipo="cpf"
              secaoIndex={secaoIndex}
              secao={secao}
              novosProdutos={novosProdutos}
              handleInputChange={handleInputChange}
              handleClearFields={handleClearFields}
              handleAddProduto={handleAddProduto}
              handleRemoveProduto={(i, t, id) => t === 'cnpj' ? handleRemoveProdutoCnpj(i, id) : handleRemoveProdutoCpf(i, id)}
              validarCPF={validarCPF}
              validarCNPJ={validarCNPJ}
            />
            {/* Total da Seção */}
            <Paper sx={{ p: 2, mt: 2, backgroundColor: 'grey.50' }}>
              <Typography variant="h6" color="primary">
                Total da Seção: R$ {formatarValorParaExibicao(calcularTotalSecao(secao))}
              </Typography>
            </Paper>
          </ContainerForm>
        ))}

        {/* //Resumo Geral
        {secoes.some(secao => secao.produtosCnpj.length > 0 || secao.produtosCpf.length > 0) && (
          <ContainerForm title="Resumo Geral das Aquisições">
            <Grid container spacing={3}>
              {secoes.map((secao, index) => {
                const totalSecao = calcularTotalSecao(secao);
                if (totalSecao > 0) {
                  return (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper sx={{ p: 2, backgroundColor: 'primary.light', color: 'white' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {secao.titulo}
                        </Typography>
                        <Typography variant="h6">
                          R$ {formatarValorParaExibicao(totalSecao)}
                        </Typography>
                        <Typography variant="caption">
                          CNPJs: {secao.produtosCnpj.length} | CPFs: {secao.produtosCpf.length}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                }
                return null;
              })}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, backgroundColor: 'success.main', color: 'white', textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Total Geral: R$ {formatarValorParaExibicao(calcularTotalGeral())}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </ContainerForm>
        )}

         //Validação e Alertas
        {calcularTotalGeral() > 0 && (() => {
          const totalRecursosAF =
            Number(formik.values.alimentacao_af_recurso_pnae || 0) +
            Number(formik.values.alimentacao_af_recurso_estado || 0) +
            Number(formik.values.alimentacao_af_recurso_prefeitura || 0);
          const totalAquisicoes = calcularTotalGeral();

          let backgroundColor = 'success.light';
          let message = `✅ Dados válidos. Total das aquisições: R$ ${formatarValorParaExibicao(totalAquisicoes)} de R$ ${formatarValorParaExibicao(totalRecursosAF)} disponíveis.`;

          if (totalRecursosAF === 0) {
            backgroundColor = 'warning.light';
            message = '⚠️ Informe os recursos aplicados na agricultura familiar antes de continuar.';
          } else if (totalAquisicoes > totalRecursosAF) {
            backgroundColor = 'error.light';
            message = `❌ O total das aquisições (R$ ${formatarValorParaExibicao(totalAquisicoes)}) não pode ser maior que os recursos aplicados (R$ ${formatarValorParaExibicao(totalRecursosAF)}).`;
          }

          return (
            <Paper sx={{ p: 2, backgroundColor, color: 'white' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {message}
              </Typography>
            </Paper>
          );
        })()} */}

        <Stack
          direction='row'
          spacing={2}
          justifyContent='center'
          alignItems='center'
          marginTop='2rem'
        >
          <Button variant='text' color='primary'
            onClick={() => {
              navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES] as string);
            }}
          >
            Voltar
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleNext}
          >
            Avançar
          </Button>
        </Stack>
      </Stack>
    </>
  );
};

export default Step1;
